import React, { Fragment, useState, FC, useEffect, useRef, useMemo } from "react";
import { Popover, Transition } from "@headlessui/react";
import { CalendarIcon } from "@heroicons/react/24/outline";
import DatePicker from "react-datepicker";
import ClearDataButton from "components/HeroSearchForm/ClearDataButton";
import DatePickerCustomHeaderTwoMonth from "components/DatePickerCustomHeaderTwoMonth";
import DatePickerCustomDay from "components/DatePickerCustomDay";
import { useBooking } from "context/BookingContext";
import { addDays, format as formatDate } from "date-fns";
import { useListingBooking } from "context/availabilitiesContext";
import { useClient } from "ClientContext";
import { useTranslation } from "react-i18next";

export interface StayDatesRangeInputProps {
  className?: string;
  onDatesChange?: () => void;
  dates: string[]; // Expecting pure "YYYY-MM-DD" strings
}

const TODAY = new Date();
TODAY.setHours(0, 0, 0, 0);
const MIN_BOOKABLE_DAYS = 30;

const BookingQuoteCalendarWMinDays: FC<StayDatesRangeInputProps> = ({
  className = "flex-1",
  onDatesChange,
  dates,
}) => {
  const { t } = useTranslation();
  const { integration_TYPE } = useClient();
  const { availabilitiesState } = useListingBooking();
  const { bookingState, setBookingState } = useBooking();

  // State to control which month the calendar should show
  const [navDate, setNavDate] = useState<Date>(TODAY);

  // Parse date-only strings to local-midnight Date objects
  const excludedDates: Date[] = useMemo(
    () =>
      dates.map((dateStr) => {
        const [year, month, day] = dateStr.split("-").map(Number);
        return new Date(year, month - 1, day);
      }),
    [dates]
  );

  // Track initial render
  const isInitialMount = useRef(true);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      setStartDate(null);
      setEndDate(null);
      setNavDate(TODAY);
      setBookingState((prev) => ({
        ...prev,
        searchParams: { ...prev.searchParams, checkIn: null, checkOut: null },
      }));
    }
  }, [setBookingState]);

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 0);
    return () => clearTimeout(timer);
  }, [availabilitiesState, integration_TYPE]);

  const onChangeDate = (datesRange: [Date | null, Date | null]) => {
    const [clickedDate, clickedEnd] = datesRange;

    // 1) If both start and end are set and user clicks a date
    if (startDate && endDate && clickedDate && !clickedEnd) {
      // Changing end only
      if (clickedDate > endDate) {
        setEndDate(clickedDate);
        setBookingState((prev) => ({
          ...prev,
          searchParams: { ...prev.searchParams, checkIn: startDate, checkOut: clickedDate },
        }));
      } else {
        // Reset start and auto-calc end
        const newStart = clickedDate;
        const autoEnd = addDays(newStart, MIN_BOOKABLE_DAYS);
        setStartDate(newStart);
        setEndDate(autoEnd);
        setNavDate(newStart); // shift UI on start change
        setBookingState((prev) => ({
          ...prev,
          searchParams: { ...prev.searchParams, checkIn: newStart, checkOut: autoEnd },
        }));
      }
      onDatesChange?.();
      return;
    }

    // 2) Full range selected initially
    if (clickedDate && clickedEnd) {
      // Always shift on initial start selection
      if (!startDate) setNavDate(clickedDate);
      let newStart = clickedDate;
      let newEnd = clickedEnd;
      if (newEnd < addDays(newStart, MIN_BOOKABLE_DAYS)) {
        newEnd = addDays(newStart, MIN_BOOKABLE_DAYS);
      }
      setStartDate(newStart);
      setEndDate(newEnd);
      setBookingState((prev) => ({
        ...prev,
        searchParams: { ...prev.searchParams, checkIn: newStart, checkOut: newEnd },
      }));
      onDatesChange?.();
      return;
    }

    // 3) First click (only start)
    if (clickedDate && !clickedEnd) {
      setNavDate(clickedDate);
      const autoEnd = addDays(clickedDate, MIN_BOOKABLE_DAYS);
      setStartDate(clickedDate);
      setEndDate(autoEnd);
      setBookingState((prev) => ({
        ...prev,
        searchParams: { ...prev.searchParams, checkIn: clickedDate, checkOut: autoEnd },
      }));
      onDatesChange?.();
      return;
    }

    // 4) Clear selection
    setStartDate(null);
    setEndDate(null);
    setNavDate(TODAY); // reset calendar view to current + next month
    setBookingState((prev) => ({
      ...prev,
      searchParams: { ...prev.searchParams, checkIn: null, checkOut: null },
    }));
    onDatesChange?.();
  };

  const renderInput = () => (
    <div className="flex-grow text-left">
      <span className="block xl:text-lg font-semibold">
        {startDate ? formatDate(startDate, "MMM dd") : "Add dates"}
        {endDate && ` - ${formatDate(endDate, "MMM dd")}`}
      </span>
      <span className="block mt-1 text-sm text-neutral-400 font-light">
        {`${t("Check in")} - ${t("Check out")}`}
      </span>
    </div>
  );

  if (loading || error || !availabilitiesState || !integration_TYPE) {
    return loading ? <div>Loading...</div> : <div>Error: {error}</div>;
  }

  return (
    <Popover className={`StayDatesRangeInput z-10 relative flex ${className}`}>
      {({ open, close }) => (
        <>
          <Popover.Button className={`flex-1 flex p-3 items-center space-x-3 focus:outline-none ${
            open ? "shadow-lg" : ""
          }`}> 
            <CalendarIcon className="w-5 h-5 lg:w-7 lg:h-7 text-neutral-300 dark:text-neutral-400" />
            {renderInput()}
            {startDate && open && (
              <ClearDataButton onClick={() => onChangeDate([null, null])} />
            )}
          </Popover.Button>

          <Transition as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <Popover.Panel className="absolute right-0 z-10 mt-3 top-full w-screen max-w-sm px-4 sm:px-0 lg:max-w-3xl">
              <div className="overflow-hidden rounded-3xl shadow-lg ring-1 ring-black ring-opacity-5 bg-white dark:bg-neutral-800 p-8">
                <DatePicker
                  key={navDate.getTime()}           // force remount when navDate changes
                  minDate={TODAY}
                  selected={startDate ?? undefined}
                  onChange={(dates) => {
                    onChangeDate(dates);
                    // Close the popover when both dates are selected
                    const [start, end] = dates;
                    if (start && end) {
                      close();
                    }
                  }}
                  startDate={startDate ?? undefined}
                  endDate={endDate ?? undefined}
                  selectsRange
                  monthsShown={2}
                  showPopperArrow={false}
                  inline
                  excludeDates={excludedDates}
                  openToDate={navDate}               // direct the UI to navDate
                  filterDate={(date) => {
                    if (startDate && !endDate) return date >= addDays(startDate, MIN_BOOKABLE_DAYS);
                    return true;
                  }}
                  renderCustomHeader={(props) => <DatePickerCustomHeaderTwoMonth {...props} />}
                  renderDayContents={(day, date) => <DatePickerCustomDay dayOfMonth={day} date={date} />} 
                />
              </div>
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
};

export default BookingQuoteCalendarWMinDays;
